import { useMemo } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { aud, formatBodyPart, hoursLabel, simTimeFull } from '@/utils/format';
import { pieceTitleOf } from '@/utils/pieceHelpers';
import { lifetimeSpendAud } from '@/utils/bookingSummary';
import type { Booking } from '@/domain/types';

// SessionLength → a human label; unknown values fall through to the raw string.
const SESSION_LABEL: Record<string, string> = {
  small: 'Small',
  'half-day': 'Half day',
  'full-day': 'Full day',
  'multi-day': 'Multi day',
};

interface MoneyRow {
  label: string;
  value: string;
  /** Render the value in blood as an amount the studio stands to lose. */
  stake?: boolean;
}

interface Props {
  customerId?: string;
  /** The booking this item hangs off - drives artist, when, session, placement. */
  booking?: Booking;
  /** Force progress against a specific piece (e.g. a no-rebook flag's pieceId). */
  pieceId?: string;
  /** Relabel the date row (e.g. "Last sat" for a stalled piece). */
  whenLabel?: string;
  /** One money fact for this decision: deposit at stake, paid, or owed. */
  money?: MoneyRow;
}

/**
 * The booking-context block shared by every Ops decision (vault Diagram 09):
 * artist, when, session, the money at stake, the piece and placement, and the
 * customer's loyalty (past sittings + lifetime value). For a mid-piece item it
 * also shows where they are against the piece's predicted sessions. Read-only -
 * it reads the live store so the numbers match the record.
 */
export default function OpsBookingSummary({
  customerId,
  booking,
  pieceId,
  whenLabel = 'When',
  money,
}: Props) {
  const bookings = useBrainStore((s) => s.bookings);
  const pieces = useBrainStore((s) => s.pieces);
  const artists = useBrainStore((s) => s.artists);
  const customers = useBrainStore((s) => s.customers);
  const salePayments = useBrainStore((s) => s.salePayments);

  const customer = customerId ? customers[customerId] : undefined;
  const artist = booking ? artists[booking.artistId] : undefined;
  const resolvedPieceId = pieceId ?? booking?.pieceId;
  const piece = resolvedPieceId ? pieces[resolvedPieceId] : undefined;

  // Lifetime sittings across the whole record - the loyalty that backs a call.
  const pastSessions = useMemo(() => {
    if (!customerId) return 0;
    return Object.values(bookings).filter(
      (b) => b.customerId === customerId && b.status === 'completed',
    ).length;
  }, [bookings, customerId]);

  // bgg4 #5: the same lifetime value the Floor and the customer record show
  // (recorded sales, never below the seeded figure), not the stale stored one.
  const lifetime = useMemo(
    () => (customer ? lifetimeSpendAud(customer, bookings, Object.values(salePayments)) : 0),
    [customer, bookings, salePayments],
  );

  const sessionLine = booking
    ? `${SESSION_LABEL[booking.sessionLength] ?? booking.sessionLength}${
        booking.durationMin ? ` · ${hoursLabel(booking.durationMin)}` : ''
      }`
    : undefined;

  const placement = booking ? formatBodyPart(booking) : '';
  const done = piece?.sessionsCompleted ?? 0;
  const total = piece?.estimatedTotalSessions;
  const showProgress = piece != null && typeof total === 'number' && total > 0;
  const left = showProgress ? total! - done : 0;

  // Nothing to show yet (e.g. an override request before a booking is attached):
  // keep the column honest rather than rendering an empty card.
  const hasContent = customer != null || booking != null || piece != null || money != null;
  if (!hasContent) {
    return (
      <p className="font-serif text-sm text-cream-faint" data-testid="owm-summary">
        No booking linked to this request yet.
      </p>
    );
  }

  return (
    <div className="panel-dark p-3.5 space-y-3" data-testid="owm-summary">
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        {artist ? <Field k="Artist" v={artist.displayName} /> : null}
        {booking ? <Field k={whenLabel} v={simTimeFull(booking.start)} /> : null}
        {sessionLine ? <Field k="Session" v={sessionLine} /> : null}
        {money ? <Field k={money.label} v={money.value} stake={money.stake} /> : null}
        {piece ? (
          <div className="col-span-2">
            <Field k="Piece" v={`${pieceTitleOf(piece)}${placement ? ` · ${placement}` : ''}`} />
          </div>
        ) : null}
      </div>

      {showProgress ? (
        <div
          className="rounded-md border border-gold-dim/25 bg-ink-black/30 p-2.5"
          data-testid="owm-piece-progress"
        >
          <p className="label-small-caps text-gold-bright">Piece progress</p>
          <div className="mt-2 flex gap-1.5" aria-hidden>
            {Array.from({ length: total! }).map((_, i) => (
              <span
                key={i}
                className={clsx(
                  'h-1.5 flex-1 rounded-full',
                  i < done ? 'bg-mint' : 'border border-dashed border-gold-dim/60',
                )}
              />
            ))}
          </div>
          <p className="font-serif text-xs text-cream-soft mt-2">
            <span className="text-cream font-semibold">
              {done} of {total} sittings done
            </span>
            {left > 0 ? (
              <>
                {' '}
                · <span className="text-cream font-semibold">{left} to go</span> to finish the piece
              </>
            ) : (
              ' · piece complete'
            )}
          </p>
        </div>
      ) : null}

      {customer ? (
        <div
          className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-gold-dim/20 pt-3"
          data-testid="owm-loyalty"
        >
          <Field k="Past sessions" v={String(pastSessions)} accent />
          <Field k="Lifetime value" v={aud(lifetime)} accent />
        </div>
      ) : null}
    </div>
  );
}

function Field({
  k,
  v,
  stake,
  accent,
}: {
  k: string;
  v: string;
  stake?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className={clsx('label-small-caps', accent && 'text-mint')}>{k}</p>
      <p
        className={clsx(
          'font-serif text-sm mt-0.5',
          stake ? 'text-blood font-semibold' : 'text-cream',
        )}
      >
        {v}
      </p>
    </div>
  );
}
